import React, { useEffect, useState } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import MultiSelectDatePicker from '../../../components/core-ui/MultiSelectDatePicker'
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Tab,
  TextField,
  Typography,
} from '@mui/material'
import { adminHolidayService } from '../../../services/admin/holidays'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { toast } from 'react-toastify'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import DeleteIcon from '@mui/icons-material/Delete'
import { getTimeInMillis } from '../../../utility/datetime'

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const YEARS = [2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030]

const ManageHolidays = ({ open, onClose }) => {
  const { user } = useAuthContext()
  const [holidays, setHolidays] = useState([])
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState(new Date().getMonth())
  const [selectedDates, setSelectedDates] = useState([])
  const [addingHolidays, setAddingHolidays] = useState([])
  const [selectedTabValue, setSelectedTabValue] = useState('1')

  useEffect(() => {
    ;(async () => {
      if (user) {
        const holidays = await adminHolidayService.getHolidays(user)
        setHolidays(holidays)
      }
    })()
  }, [user, selectedTabValue])

  useEffect(() => {
    const holidayData = holidays.find((it) => it.y == year && it.m == month + 1)
    const addedData = holidayData?.d?.map((day, index) => ({
      date: new Date(holidayData.y, holidayData.m - 1, day.day),
      name: day.name,
      id: getTimeInMillis() + index,
    }))
    setSelectedDates(addedData?.map((it) => it.date))
    setAddingHolidays(addedData || [])
  }, [year, month, holidays])

  const saveAddingHolidays = async () => {
    // write logic to save holidays to db
    try {
      const data = {
        year,
        month: month + 1,
        days: JSON.stringify(
          addingHolidays.map((it) => ({ day: new Date(it.date).getDate(), name: it.name }))
        ),
      }
      await adminHolidayService.addHolidays(user, data)
      setSelectedTabValue('1')
      toast.success('Holidays have been updated!')
    } catch (error) {
      toast.error('Something went wrong!')
    }
  }

  const handleChange = (_, newValue) => {
    setSelectedTabValue(newValue)
  }

  return (
    <DialogWrapper open={open} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 12, gap: 8 }}>
        <Typography variant="h6">Manage Holidays</Typography>
        <Select size="small" onChange={(e) => setYear(e.target.value)} value={year}>
          {YEARS.map((it) => (
            <MenuItem value={it}>{it}</MenuItem>
          ))}
        </Select>
      </div>
      <TabContext value={selectedTabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            aria-label="Holiday Management"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Holidays" value="1" />
            <Tab label="Add/Remove Holidays" value="2" />
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ padding: 0, paddingTop: 2 }}>
          {holidays
            ?.filter((it) => it.y == year)
            ?.sort((a, b) => a.m - b.m)
            ?.map((it) => {
              return (
                <Box>
                  <Typography variant="h6">{`${MONTHS[it.m - 1]} ${it.y}`}</Typography>
                  {it.d?.map((day) => {
                    return (
                      <Box>
                        <Typography variant="p">{`${day.day.toString().padStart(2, '0')} - ${
                          day.name
                        }`}</Typography>
                      </Box>
                    )
                  })}
                </Box>
              )
            })}
        </TabPanel>
        <TabPanel value="2" sx={{ padding: 0, paddingTop: 2 }}>
          <Box>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Select fullWidth size="small" onChange={(e) => setYear(e.target.value)} value={year}>
                {YEARS.map((it) => (
                  <MenuItem value={it}>{it}</MenuItem>
                ))}
              </Select>
              <Select
                fullWidth
                size="small"
                onChange={(e) => setMonth(MONTHS.findIndex((it) => it == e.target.value))}
                value={MONTHS[month]}
              >
                {MONTHS.map((it) => (
                  <MenuItem value={it}>{it}</MenuItem>
                ))}
              </Select>
            </div>
            <Divider style={{ marginTop: 16 }} />
            {addingHolidays?.map((it) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                <MultiSelectDatePicker
                  selectedDates={selectedDates}
                  onDatesChange={setSelectedDates}
                  date={it.date}
                  setDate={(date) => {
                    setAddingHolidays(
                      addingHolidays.map((adding) =>
                        adding.id == it.id ? { ...adding, date: date } : adding
                      )
                    )
                  }}
                  label="Holiday Date"
                  year={year}
                  month={month}
                ></MultiSelectDatePicker>
                <TextField
                  style={{ marginTop: 4 }}
                  fullWidth
                  size="small"
                  label="Holiday Name"
                  value={it.name}
                  onChange={(e) => {
                    setAddingHolidays(
                      addingHolidays.map((adding) =>
                        adding.id == it.id ? { ...adding, name: e.target.value } : adding
                      )
                    )
                  }}
                />
                <IconButton
                  onClick={() => {
                    setAddingHolidays(addingHolidays.filter((d) => d.id != it.id))
                  }}
                >
                  <DeleteIcon size="small" />
                </IconButton>
              </div>
            ))}
            {year ? (
              <div
                style={{
                  display: 'flex',
                  columnGap: 16,
                  width: '100%',
                  marginTop: 16,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    setAddingHolidays([
                      ...addingHolidays,
                      { id: new Date().getTime(), date: '', name: '' },
                    ])
                  }}
                >
                  Add Row
                </Button>
              </div>
            ) : null}
            {selectedDates?.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  columnGap: 16,
                  width: '100%',
                  justifyContent: 'flex-end',
                  marginTop: 16,
                }}
              >
                <Button size="small" variant="contained" onClick={() => setAddingHolidays([])}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={saveAddingHolidays}
                >
                  Save
                </Button>
              </div>
            ) : null}
          </Box>
        </TabPanel>
      </TabContext>
    </DialogWrapper>
  )
}

export default ManageHolidays
